export const usePushNotifications = (options = {}) => {
  const {
    applicationServerKey,
    uid,
    product_id = undefined,
    product_name = undefined,
    adv_id = undefined,
  } = options

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  const checkNotificationPermission = () => {
    return new Promise<void>((resolve, reject) => {
      if (Notification.permission === 'denied') {
        return reject(new Error('Push messages are blocked.'))
      }
      if (Notification.permission === 'granted') {
        return resolve()
      }
      if (Notification.permission === 'default') {
        return Notification.requestPermission().then((result) => {
          if (result !== 'granted') {
            reject(new Error('Bad permission result'))
          } else {
            resolve()
          }
        })
      }
      return reject(new Error('Unknown permission'))
    })
  }

  const sendSubscriptionToServer = async (
    subscription: PushSubscription,
    method: 'POST' | 'PUT' | 'DELETE'
  ) => {
    const contentEncoding = (PushManager.supportedContentEncodings || ['aesgcm'])[0]
    const jsonSubscription = subscription.toJSON()
    const payload: Record<string, any> = {
      ...jsonSubscription,
      contentEncoding,
      uid,
    }

    if (product_id !== undefined && product_id !== null) {
      payload.product_id = product_id
      payload.adv_id = adv_id
      payload.product_name = product_name
    }

    await $fetch('/market/api/push/save', {
      method,
      body: payload,
    })

    return subscription
  }

  const subscribe = async () => {
    if (!applicationServerKey) {
      throw new Error('applicationServerKey is required')
    }

    await checkNotificationPermission()
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
    })
    await sendSubscriptionToServer(subscription, 'POST')
    return subscription
  }

  const waitForServiceWorker = (timeoutMs = 8000) => {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<ServiceWorkerRegistration | null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs)
    }),
  ])
}

const updateSubscription = async () => {
    const registration = await waitForServiceWorker()
    if (!registration) {
      return null
    }
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      if (Notification.permission === 'granted' && applicationServerKey) {
        const freshSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
        })
        await sendSubscriptionToServer(freshSubscription, 'POST')
        return freshSubscription
      }
      return null
    }
    await sendSubscriptionToServer(subscription, 'PUT')
    return subscription
  }

  const unsubscribe = async () => {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      return
    }
    await sendSubscriptionToServer(subscription, 'DELETE')
    await subscription.unsubscribe()
  }

  return {
    subscribe,
    updateSubscription,
    unsubscribe,
    checkNotificationPermission,
  }
}
